import React, { useState, useEffect, useCallback } from 'react';
import { 
  Select, MenuItem, TextField, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Typography, Box, Chip, Dialog, DialogTitle, DialogContent, 
  DialogActions, Button, Checkbox
} from '@mui/material';
import { adminService } from '../../../services/adminService';
import { useSnackbar } from 'notistack';

// State code mapping
const STATES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DC': 'District of Columbia', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming'
};

const SchoolSearch = ({ 
  open, 
  onClose, 
  onSchoolsSelected, 
  title = 'Search Schools',
  // Legacy props for backward compatibility
  onSchoolSelect, 
  selectedSchools: legacySelectedSchools, 
  schools: legacySchools, 
  setSchools: legacySetSchools 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchFilters, setSearchFilters] = useState({
    state: '',
    city: '',
    searchQuery: ''
  });
  const [schools, setSchools] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle both dialog mode and legacy component mode
  // Check if 'open' prop was passed (even if false) vs not passed at all
  const isDialogMode = open !== undefined;
  const schoolsToUse = isDialogMode ? schools : (legacySchools || []);
  const selectedToUse = isDialogMode ? selectedSchools : (legacySelectedSchools || new Set());

  const handleSearch = useCallback(async () => {
    if (!searchFilters.state) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminService.searchSchools({
        state: searchFilters.state,
        city: searchFilters.city,
        searchQuery: searchFilters.searchQuery,
        limit: 100
      }, enqueueSnackbar);

      const sortedSchools = (response?.data?.schools || []).sort((a, b) => a.name.localeCompare(b.name));
      
      if (isDialogMode) {
        setSchools(sortedSchools);
      } else if (legacySetSchools) {
        legacySetSchools(sortedSchools);
      }
    } catch (err) {
      console.error('School search error:', err);
      setError(err.message || 'Failed to fetch schools');
      if (isDialogMode) {
        setSchools([]);
      } else if (legacySetSchools) {
        legacySetSchools([]);
      }
    } finally {
      setLoading(false);
    }
  }, [searchFilters, isDialogMode, legacySetSchools, enqueueSnackbar]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchFilters.state) {
        handleSearch();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchFilters, handleSearch]);

  const isSelected = (school) => {
    if (isDialogMode) {
      return selectedSchools.has(school.uuid || school.ncessch);
    } else {
      return selectedToUse.has(school.ncessch);
    }
  };

  const handleSchoolToggle = (school) => {
    if (isDialogMode) {
      const schoolId = school.uuid || school.ncessch;
      const newSelected = new Set(selectedSchools);
      if (newSelected.has(schoolId)) {
        newSelected.delete(schoolId);
      } else {
        newSelected.add(schoolId);
      }
      setSelectedSchools(newSelected);
    } else if (onSchoolSelect) {
      onSchoolSelect(school.ncessch);
    }
  };

  const handleConfirm = () => {
    if (onSchoolsSelected && isDialogMode) {
      const selectedSchoolsArray = schoolsToUse.filter(school => 
        selectedSchools.has(school.uuid || school.ncessch)
      );
      onSchoolsSelected(selectedSchoolsArray);
    }
    onClose?.();
  };

  const searchContent = (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Select
          value={searchFilters.state}
          onChange={(e) => setSearchFilters(prev => ({ ...prev, state: e.target.value }))}
          sx={{ minWidth: 200 }}
          displayEmpty
        >
          <MenuItem value="">Select State</MenuItem>
          {Object.entries(STATES).map(([code, name]) => (
            <MenuItem key={code} value={code}>
              {name} ({code})
            </MenuItem>
          ))}
        </Select>

        <TextField
          placeholder="Enter city..."
          value={searchFilters.city}
          onChange={(e) => setSearchFilters(prev => ({ ...prev, city: e.target.value }))}
          sx={{ minWidth: 200 }}
        />

        <TextField
          placeholder="Search schools..."
          value={searchFilters.searchQuery}
          onChange={(e) => setSearchFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
          sx={{ minWidth: 300 }}
        />
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {isDialogMode && <TableCell padding="checkbox">Select</TableCell>}
              <TableCell>School Name</TableCell>
              <TableCell>Display Name (EDC)</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>NCES ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isDialogMode ? 6 : 5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : !schoolsToUse || schoolsToUse.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isDialogMode ? 6 : 5} align="center">
                  {searchFilters.state ? 'No schools found' : 'Select a state to begin'}
                </TableCell>
              </TableRow>
            ) : (
              schoolsToUse.map((school) => (
                <TableRow
                  key={school.uuid || school.ncessch}
                  hover
                  onClick={() => handleSchoolToggle(school)}
                  selected={isSelected(school)}
                  sx={{ cursor: 'pointer' }}
                >
                  {isDialogMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected(school)}
                        onChange={() => handleSchoolToggle(school)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {school.name}
                    {school.isSplit && (
                      <Box>
                        <Chip 
                          size="small" 
                          label={`Split School ${school.ncessch && school.ncessch.includes('-') ? 
                            `- ${school.ncessch.split('-')[1].toUpperCase()}` : ''}`}
                          color="primary" 
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{school.display_name || '-'}</TableCell>
                  <TableCell>{school.city}</TableCell>
                  <TableCell>{STATES[school.state] || school.state}</TableCell>
                  <TableCell>
                    {school.ncessch || (school.isSplit ? `${school.parentNcessch}-${school.splitType?.toLowerCase()}` : 'N/A')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // If this is dialog mode, wrap in Dialog
  if (isDialogMode) {
    // Only render dialog content when actually open
    if (!open) {
      return null;
    }
    
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {searchContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleConfirm}
            disabled={selectedSchools.size === 0}
          >
            Add {selectedSchools.size} School{selectedSchools.size !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Otherwise return the content directly (legacy mode)
  return searchContent;
};

export default SchoolSearch;